interface IRAF {
  timers: { [key: symbol]: number | null };
  setTimeout(cb: () => void, interval: number): symbol;
  clearTimeout(timerId: symbol): void;
  setInterval(cb: () => void, interval: number): symbol;
  clearInterval(timerId: symbol): void;
}

interface IRafFpsScheduler<T> {
  schedule(payload?: T): void;
  cancel(): void;
}

/**
 * 基于 requestAnimationFrame 的计时器
 */
export const RAF: IRAF = {
  timers: {},

  setTimeout(cb, interval) {
    const timerId = Symbol(`timeout_${Object.keys(this.timers).length + 1}`);
    let now: () => number = Date.now;
    let stime: number = now();
    let etime: number = stime;

    let loop = () => {
      this.timers[timerId] = requestAnimationFrame(loop);
      etime = now();
      if (etime - stime >= interval) {
        cb();
        cancelAnimationFrame(this.timers[timerId] as number);
        this.clearTimeout(timerId);
      }
    };

    this.timers[timerId] = requestAnimationFrame(loop);
    return timerId;
  },

  clearTimeout(timerId) {
    if (this.timers[timerId]) {
      cancelAnimationFrame(this.timers[timerId] as number);
      this.timers[timerId] = null;
    }
  },

  setInterval(cb, interval) {
    const timerId = Symbol(`interval_${Object.keys(this.timers).length + 1}`);
    let now: () => number = Date.now;
    let stime: number = now();
    let etime: number = stime;

    let loop = () => {
      this.timers[timerId] = requestAnimationFrame(loop);
      etime = now();
      if (etime - stime >= interval) {
        stime = now();
        etime = stime;
        cb();
      }
    };

    this.timers[timerId] = requestAnimationFrame(loop);
    return timerId;
  },

  clearInterval(timerId) {
    if (this.timers[timerId]) {
      cancelAnimationFrame(this.timers[timerId] as number);
      this.timers[timerId] = null;
    }
  },
};

export const createRafFpsScheduler = <T = void>(
  callback: (payload?: T) => void,
  fps: number = 60
): IRafFpsScheduler<T> => {
  const frameInterval = 1000 / Math.max(1, fps);
  let rafId: number | null = null;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastRunAt = 0;
  let hasPendingPayload = false;
  let latestPayload: T | undefined;

  const run = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      if (!hasPendingPayload) return;
      hasPendingPayload = false;
      lastRunAt = performance.now();
      callback(latestPayload);
    });
  };

  const schedule = (payload?: T) => {
    latestPayload = payload;
    hasPendingPayload = true;

    const now = performance.now();
    const elapsed = now - lastRunAt;

    if (elapsed >= frameInterval) {
      run();
      return;
    }

    if (timeoutId === null && rafId === null) {
      timeoutId = setTimeout(() => {
        timeoutId = null;
        run();
      }, frameInterval - elapsed);
    }
  };

  const cancel = () => {
    hasPendingPayload = false;
    latestPayload = undefined;
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };

  return {
    schedule,
    cancel
  };
};
